/**
 * 支付/会员页公用展示组件：VIP 头卡、套餐选择网格、会员权益。
 * 微信会员页与支付宝收银台共用，保证两端视觉一致。
 */

import React from 'react';
import styles from './PayParts.module.scss';

export interface PayProduct {
  type: string;
  appName: string;
  appNameCh: string | null;
  months: number;
  price: number;
  discount: number;
  originPrice: number;
}

// 按月数推断套餐名（后端 appNameCh 缺失时兜底）
const MONTH_NAME_MAP: { [k: number]: string } = {
  1: '月度会员',
  3: '季度会员',
  6: '半年会员',
  12: '年度会员',
};

// 推荐套餐（高亮 + “超值”角标）的月数
export const RECOMMEND_MONTHS = 6;

// 取套餐展示名：优先后端 appNameCh 的首段，其次按月数兜底
export function getPackageName(obj: PayProduct): string {
  if (obj.appName === 'life') {
    return '终身会员';
  }
  const ch = (obj.appNameCh || '').split('-')[0];
  if (ch) {
    return ch;
  }
  return MONTH_NAME_MAP[obj.months] || `包${obj.months}个月`;
}

// 会员权益（内联 SVG 图标，避免依赖图标字体）
const BENEFITS = [
  {
    bg: '#fdecea',
    color: '#e54d42',
    title: '错题集回顾',
    desc: '随时查看孩子做错的题目',
    icon: (
      <path d="M4 5a2 2 0 0 1 2-2h7v16H6a2 2 0 0 0-2 2V5zM20 5a2 2 0 0 0-2-2h-5v16h5a2 2 0 0 1 2 2V5z" />
    ),
  },
  {
    bg: '#f0ebff',
    color: '#7a5cd0',
    title: '智能出题',
    desc: '按薄弱点个性化推荐练习',
    icon: (
      <path d="M12 3l1.8 4.6L18 9.4l-4.2 1.8L12 16l-1.8-4.8L6 9.4l4.2-1.8L12 3zM19 14l.8 2L22 16.8l-2.2.8L19 20l-.8-2.4L16 16.8l2.2-.8L19 14z" />
    ),
  },
  {
    bg: '#e7f5ee',
    color: '#0F6E56',
    title: '免除广告',
    desc: '纯净无干扰的学习体验',
    icon: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M5.6 5.6l12.8 12.8" />
      </>
    ),
  },
];

interface VipHeroProps {
  tag?: string;
  subtitle?: string;
}

export function VipHero({ tag = '限时优惠', subtitle = '错题集回顾 · 智能出题 · 免除广告' }: VipHeroProps) {
  return (
    <div className={styles.card}>
      <div className={styles.hero}>
        { tag && <span className={styles.heroTag}>{tag}</span> }
        <div className={styles.heroIcon}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
            <path d="M3 8l4 3 5-6 5 6 4-3-2 11H5L3 8z" fill="#e0a32e" stroke="#b9821b" strokeWidth="1" strokeLinejoin="round" />
            <rect x="5" y="18.6" width="14" height="2.4" rx="1.2" fill="#b9821b" />
          </svg>
        </div>
        <div>
          <div className={styles.heroMain}>小学僮 VIP 会员</div>
          <div className={styles.heroSub}>{subtitle}</div>
        </div>
      </div>
    </div>
  );
}

interface PackageGridProps {
  productList: PayProduct[];
  month: number;
  onSelect: (months: number) => void;
}

export function PackageGrid({ productList, month, onSelect }: PackageGridProps) {
  return (
    <div className={styles.card}>
      <div className={styles.secTitle}><span className={styles.secBar} /><span>选择套餐</span></div>
      <div className={styles.pkgGrid}>
        {
          productList.map((obj) => {
            const selected = obj.months === month;
            const priceStr = (obj.price / 100).toFixed(2);
            const hasDiscount = obj.originPrice && obj.price < obj.originPrice;
            const originStr = hasDiscount ? (obj.originPrice / 100).toFixed(2) : '';
            const discountTenth = hasDiscount ? Math.round((obj.price / obj.originPrice) * 10) : 0;
            const recommend = obj.months === RECOMMEND_MONTHS;
            let badge = '';
            if (recommend && hasDiscount) {
              badge = `超值 ${discountTenth}折`;
            } else if (recommend) {
              badge = '超值';
            } else if (hasDiscount) {
              badge = `${discountTenth}折`;
            }
            const optClass = `${styles.pkgOpt} ${selected ? styles.pkgOptOn : ''}`;
            return (
              <div key={obj.months} className={optClass} onClick={() => onSelect(obj.months)}>
                { badge && <span className={styles.pkgBadge}>{badge}</span> }
                <div className={styles.pkgName}>{getPackageName(obj)}</div>
                <div className={styles.pkgPrice}><span className={styles.yen}>¥</span>{priceStr}</div>
                <div className={styles.pkgOrigin}>{originStr && `¥${originStr}`}</div>
                { selected && <span className={styles.pkgCheck} /> }
              </div>
            );
          })
        }
      </div>
    </div>
  );
}

export function MemberBenefits() {
  return (
    <div className={styles.card}>
      <div className={styles.secTitle}><span className={styles.secBar} /><span>会员权益</span></div>
      {
        BENEFITS.map((b, i) => (
          <div key={b.title} className={styles.benefit} style={i === 0 ? { borderTop: 'none' } : undefined}>
            <div className={styles.benefitIcon} style={{ background: b.bg }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={b.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                {b.icon}
              </svg>
            </div>
            <div>
              <div className={styles.benefitTitle}>{b.title}</div>
              <div className={styles.benefitDesc}>{b.desc}</div>
            </div>
          </div>
        ))
      }
    </div>
  );
}
